import { forwardRef } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { cn } from "../../utils";
import { triggerVariants } from "../dateTimePickerStyles";
import type { DateTimePickerTriggerProps } from "../types";

/** Default trigger button, replaceable via `slots.trigger`. Forwards its ref. */

type DateTimePickerTriggerElementProps = DateTimePickerTriggerProps &
  Omit<React.ComponentPropsWithoutRef<"button">, "onClear" | "className">;

export const DateTimePickerTrigger = forwardRef<HTMLButtonElement, DateTimePickerTriggerElementProps>(
  function DateTimePickerTrigger(
    {
      displayValue,
      placeholder,
      hasValue,
      open,
      disabled,
      clearable,
      onClear,
      size,
      state,
      className,
      Icon = CalendarIcon,
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-part="trigger"
        data-state={hasValue ? "filled" : "empty"}
        className={cn(triggerVariants({ size, state }), className)}
        {...rest}
      >
        <span className={cn("flex-1 text-left", !hasValue && "text-muted-foreground")}>
          {displayValue || placeholder}
        </span>

        {clearable && hasValue && !disabled ? (
          // A nested <button> is invalid HTML, so this is a role="button" span.
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear value"
            data-part="clear"
            onClick={onClear}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClear(event);
              }
            }}
            className="grid size-5 place-items-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-4" aria-hidden />
          </span>
        ) : null}

        {/* Decorative: the accessible name comes from `aria-label` on the button. */}
        <span aria-hidden className="flex shrink-0 items-center">
          <Icon className="size-4 text-muted-foreground" />
        </span>
      </button>
    );
  },
);
